import {
  CommitMenuExtractionRequest,
  CreateMenuExtractionRequest,
  MenuExtraction,
  MenuExtractionOutput,
  UpdateMenuExtractionRequest,
} from '../types/menu';
import { supabase } from './supabase';

/**
 * Fetch pending extractions for an outlet
 */
export async function fetchPendingExtractions(
  outletId: string,
): Promise<MenuExtraction[]> {
  const { data, error } = await supabase
    .from('menu_extractions')
    .select('*')
    .eq('outlet_id', outletId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Fetch all extractions for an outlet
 */
export async function fetchAllExtractions(
  outletId: string,
): Promise<MenuExtraction[]> {
  const { data, error } = await supabase
    .from('menu_extractions')
    .select('*')
    .eq('outlet_id', outletId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
}

// Remove null bytes and C0/C1 control chars (except \t \n \r) that Postgres rejects.
/* eslint-disable no-control-regex */
const sanitizeForPostgres = (text: string): string =>
  text.replace(/\x00/g, '').replace(/[\x01-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
/* eslint-enable no-control-regex */

/**
 * Create a new menu extraction record
 */
export async function createMenuExtraction(
  request: CreateMenuExtractionRequest,
): Promise<MenuExtraction> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('menu_extractions')
    .insert({
      outlet_id: request.outlet_id,
      raw_text: sanitizeForPostgres(request.raw_text),
      file_name: request.file_name,
      file_type: request.file_type,
      created_by: user?.id,
      status: 'pending',
      version: 1,
    })
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update extraction status or output
 */
export async function updateMenuExtraction(
  request: UpdateMenuExtractionRequest,
): Promise<MenuExtraction> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const updateData: Record<string, unknown> = {};

  if (request.json_output !== undefined) {
    updateData.json_output = request.json_output;
  }

  if (request.status !== undefined) {
    updateData.status = request.status;

    if (request.status === 'reviewed' || request.status === 'declined') {
      updateData.reviewed_by = user?.id;
      updateData.reviewed_at = new Date().toISOString();
    }
  }

  const { data, error } = await supabase
    .from('menu_extractions')
    .update(updateData)
    .eq('id', request.id)
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}

/**
 * Process text with AI to extract menu items
 * This calls the Supabase edge function
 */
export async function extractMenuWithAI(
  extractionId: string,
  rawText: string,
  departmentNames: string[],
): Promise<MenuExtractionOutput> {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session) {
    throw new Error(
      'You must be logged in to use AI extraction. Please sign in again.',
    );
  }

  const { data, error } = await supabase.functions.invoke('extract-menu', {
    body: {
      extraction_id: extractionId,
      raw_text: rawText,
      departments: departmentNames,
    },
  });

  if (error) {
    if (
      error.message?.includes('401') ||
      error.message?.includes('Unauthorized')
    ) {
      throw new Error('Authentication failed. Please sign in again.');
    }
    throw new Error(error.message || 'AI extraction failed');
  }

  return data as MenuExtractionOutput;
}

/**
 * Commit extraction to live menu tables
 */
export async function commitMenuExtraction(
  request: CommitMenuExtractionRequest,
): Promise<{
  success: boolean;
  itemsCreated: number;
  categoriesCreated: number;
}> {
  const { data, error } = await supabase.functions.invoke('commit-menu', {
    body: request,
  });

  if (error) {
    throw new Error(error.message || 'Commit failed');
  }

  return data;
}

/**
 * Decline an extraction and create a new version for re-extraction
 */
export async function declineAndRerunExtraction(
  extractionId: string,
  outletId: string,
): Promise<MenuExtraction> {
  await updateMenuExtraction({
    id: extractionId,
    status: 'declined',
  });

  const { data: currentExtraction, error: fetchError } = await supabase
    .from('menu_extractions')
    .select('version, raw_text, file_name, file_type')
    .eq('id', extractionId)
    .single();

  if (fetchError || !currentExtraction) {
    throw new Error('Failed to fetch current extraction');
  }

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('menu_extractions')
    .insert({
      outlet_id: outletId,
      raw_text: currentExtraction.raw_text,
      file_name: currentExtraction.file_name,
      file_type: currentExtraction.file_type,
      created_by: user?.id,
      status: 'pending',
      version: currentExtraction.version + 1,
    })
    .select()
    .single();

  if (error) {
    throw new Error(error.message);
  }

  return data;
}
